import { useAppRootContext } from '@jusbrasil-web/shared-app-root-provider'
import { useUserSession } from '@jusbrasil-web/shared-user-session'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useCallback, useMemo, useRef } from 'react'

import { EXPERIMENT_PARTICIPATE_EVENT_NAME, FEATURE_FLAGS_INTERNAL_STATE } from '../constants'
import {
  AppPropsWithFeatureFlags,
  FeatureFlagsContextTypeInitialized,
  FeatureFlagsProviderInitializedProps,
  FeatureFlagsProviderProps,
  FeatureKey,
  FeatureKeyValueMap,
} from '../types'
import { FeatureFlagsContext } from './feature-flags-context'

const isDevelopment = process.env.NODE_ENV === 'development'
let hasWarnedMissingState = false

function FeatureFlagsProviderInitialized<F extends FeatureKeyValueMap>({
  children,
  featureFlags,
  trackedExperiments,
}: FeatureFlagsProviderInitializedProps<F>) {
  const session = useUserSession()
  const sentExperimentsRef = useRef(new Set<string>())

  const sendExperimentParticipation = useCallback(
    (key: FeatureKey) => {
      const tracked = trackedExperiments[key]
      if (typeof window !== 'undefined' && tracked && !sentExperimentsRef.current.has(key)) {
        sentExperimentsRef.current.add(key)
        // Defer the side effect to outside the render cycle
        queueMicrotask(() => {
          sendMetrics(EXPERIMENT_PARTICIPATE_EVENT_NAME, session?.pid?.toString(), {
            experiment: tracked.experimentKey,
            alternative: tracked.variationName,
            device_id: session?.jdid ?? null,
          })
        })
      }
    },
    [trackedExperiments, session]
  )

  const getFeatureValue = useCallback(
    <K extends FeatureKey>(key: K): F[K] => {
      const value = featureFlags[key]
      sendExperimentParticipation(key)
      return value
    },
    [featureFlags, sendExperimentParticipation]
  )

  const inExperiment = useCallback(
    // Read-only: reports bucketing without firing the participation event.
    (key: FeatureKey) => Boolean(trackedExperiments[key]),
    [trackedExperiments]
  )

  const value: FeatureFlagsContextTypeInitialized<F> = useMemo(
    () => ({ initialized: true, getFeatureValue, inExperiment }),
    [getFeatureValue, inExperiment]
  )

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
}

export function FeatureFlagsProvider<F extends FeatureKeyValueMap>({
  children,
  pageProps,
}: FeatureFlagsProviderProps<F>) {
  const { getPageProp } = useAppRootContext<AppPropsWithFeatureFlags<F>>()

  const internalState =
    getPageProp(FEATURE_FLAGS_INTERNAL_STATE) ?? pageProps[FEATURE_FLAGS_INTERNAL_STATE]

  if (internalState) {
    return (
      <FeatureFlagsProviderInitialized
        featureFlags={internalState.featureFlags}
        trackedExperiments={internalState.trackedExperiments}
      >
        {children}
      </FeatureFlagsProviderInitialized>
    )
  }

  if (isDevelopment && !hasWarnedMissingState) {
    hasWarnedMissingState = true
    console.warn(
      '[shared-feature-flags] FeatureFlagsProvider rendered without feature flags state. Make sure withFeatureFlags() is called in getServerSideProps.'
    )
  }
  return children
}
